import graphql from "babel-plugin-relay/macro";
import {
  loadQuery,
  PreloadedQuery,
  useFragment,
  usePreloadedQuery,
} from "react-relay";

import styled from "styled-components";
import IconButton from "../components/Buttons/IconButton";

import { InspectionQuery as InspectionQueryType } from "./__generated__/InspectionQuery.graphql";
import { Inspection_header$key } from "./__generated__/Inspection_header.graphql";

import InspectionForm, {
  InspectionFormFallback,
} from "../components/InspectionForm";

import { ReactComponent as SmallDocumentIcon } from "../assets/document-small.svg";
import { useLoaderData } from "react-router-dom";
import RelayEnvironment from "../relay/RelayEnvironment";
import { memo, Suspense, useState } from "react";
import { Link } from "react-router-dom";
import { FormState } from "react-hook-form";
import useOnlineStatus from "../hooks/useOnlineStatus";
import { getWindowHeight } from "../utils";

const InspectionQuery = graphql`
  query InspectionQuery($id: ID!) {
    inspection(id: $id) {
      id
      inspectionfield {
        id
        externalId
        year
        ...InspectionFormOverview
        field {
          id
          previousId
        }
      }
      inspectionform {
        name
      }
      ...Inspection_header
      ...InspectionForm
    }
  }
`;

export const loader = ({ params }: any) => {
  return loadQuery<InspectionQueryType>(RelayEnvironment, InspectionQuery, {
    id: params.id!,
  });
};

export const InspectionContainer = memo(function InspectionComp() {
  return (
    <Suspense fallback={<InspectionFallback />}>
      <Inspection />
    </Suspense>
  );
});

const Inspection = memo(function Inspection() {
  const queryRef = useLoaderData() as PreloadedQuery<InspectionQueryType>;
  const data = usePreloadedQuery<InspectionQueryType>(
    InspectionQuery,
    queryRef
  );
  const [isFormDirty, setIsFormDirty] = useState(false);

  const handleFormStateChange = (formState: FormState<any>) => {
    setIsFormDirty(formState.isDirty);
  };

  if (!data.inspection) return null;
  return (
    <Container>
      <HeaderContainer
        inspection={data?.inspection}
        isNavigationBlocked={isFormDirty}
      />
      {data?.inspection && (
        <InspectionForm
          inspection={data.inspection}
          onFormStateChange={handleFormStateChange}
        />
      )}
    </Container>
  );
});

export const InspectionFallback = () => {
  function cancel() {}
  return (
    <Container>
      <Header>
        <IconButton
          icon="left"
          onClick={cancel}
          size="large"
          tooltip="Annuleer"
        />
        <TitleContainer>
          <Title>
            <SmallDocumentIcon style={{ paddingRight: 16 }} />
            <TitleFallback />
          </Title>
          <Subtitle>
            <SubtitleFallback />
          </Subtitle>
        </TitleContainer>
      </Header>
      <InspectionFormFallback />
    </Container>
  );
};

function HeaderContainer(props: {
  inspection: Inspection_header$key;
  isNavigationBlocked: boolean;
}) {
  const isOnline = useOnlineStatus();

  const data = useFragment(
    graphql`
      fragment Inspection_header on InspectionNode {
        inspectionform {
          name
        }
        inspectionfield {
          id
          externalId
          managementPackageSetting {
            displayNameAlt
          }
          member {
            name
          }
        }
      }
    `,
    props.inspection
  );

  return (
    <Header>
      <Link to={`/b/${data?.inspectionfield?.id}`}>
        <IconButton
          icon="left"
          size="large"
          tooltip="Annuleer"
          onClick={(e) => {
            if (
              props.isNavigationBlocked &&
              !window.confirm(
                "Let op: er zijn niet-opgeslagen gegevens. Weet je zeker dat je deze ongedaan wilt maken?"
              )
            )
              e.preventDefault();
          }}
        />
      </Link>
      <TitleContainer>
        <Title>
          <SmallDocumentIcon style={{ paddingRight: 16 }} />
          {data.inspectionform?.name || ""}
        </Title>
        <Subtitle>
          {data.inspectionfield?.externalId}
          &nbsp;-&nbsp;
          {data.inspectionfield?.managementPackageSetting?.displayNameAlt}
          &nbsp;-&nbsp;
          {data.inspectionfield?.member?.name || ""}
        </Subtitle>
      </TitleContainer>
      <OfflineMessageContainer>
        {!isOnline && (
          <OfflineMessage>
            Geen internet connectie... Formulier opslaan momenteel niet
            mogelijk.
          </OfflineMessage>
        )}
      </OfflineMessageContainer>
    </Header>
  );
}

const Container = styled.div`
  height: ${getWindowHeight()};
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  background: #0075bc;
  display: flex;
  flex-direction: row;
  padding-top: 12px;
  padding-bottom: 12px;
  padding-left: 20px;
  align-items: center;
`;

const Title = styled.span`
  /* Tablet/H2 */
  font-family: "Roboto";
  font-style: normal;
  font-weight: 700;
  font-size: 24px;
  line-height: 120%;

  /* or 29px */
  display: flex;
  align-items: center;

  /* Wit */
  color: #ffffff;
  padding-bottom: 4px;
`;
const TitleFallback = styled.span`
  width: 60px;
  height: 24px;
  background-color: rgba(255, 255, 255, 0.5);
  animation: background-color-change 3s infinite;
  @keyframes background-color-change {
    0% {
      background-color: rgba(255, 255, 255, 0.5);
    }
    50% {
      background-color: rgba(255, 255, 255, 0.2);
    }
    100% {
      background-color: rgba(255, 255, 255, 0.5);
    }
  }
`;
const Subtitle = styled.span`
  /* Tablet/Label regular */
  font-family: "Roboto";
  font-style: normal;
  font-weight: 400;
  font-size: 13px;
  line-height: 120%;

  /* or 16px */
  display: flex;
  align-items: center;

  /* Wit */
  color: #ffffff;
`;
const SubtitleFallback = styled.span`
  width: 330px;
  height: 16px;
  background-color: rgba(255, 255, 255, 0.5);
  animation: background-color-change 3s infinite;
  @keyframes background-color-change {
    0% {
      background-color: rgba(255, 255, 255, 0.5);
    }
    50% {
      background-color: rgba(255, 255, 255, 0.2);
    }
    100% {
      background-color: rgba(255, 255, 255, 0.5);
    }
  }
`;

const TitleContainer = styled.div`
  padding-left: 24px;
  display: flex;
  flex-direction: column;
`;

const OfflineMessageContainer = styled.span`
  display: flex;
  justify-content: center;
  width: 300px;
  margin: 0 auto;
`;

const OfflineMessage = styled.div`
  background-color: #ffcc00;
  color: #333;
  text-align: center;
  padding: 8px;
  font-size: 14px;
`;

export default InspectionContainer;
